import { Injectable } from '@angular/core';
import {
  Campaign,
  CampaignEventMetadata,
  CampaignEventSource,
  CampaignEventType,
  EligibleCampaignList,
  EligibleCampaignListItem,
  HighlightedCampaignEligibility
} from '../interface/campaign';
import { Utilities } from '../class/utilities';
import { Company } from '../interface/company';
import { MkgUser } from '../interface/user';
import { buildWhatsAppLink } from '../shared/utils/whatsapp-link.util';
import { CampaignsService } from './campaigns.service';

interface StoredHighlightedCampaignEligibility {
  storedAt: string;
  eligibility: HighlightedCampaignEligibility;
}

interface StoredEligibleCampaignList {
  storedAt: string;
  list: EligibleCampaignList;
}

@Injectable({
  providedIn: 'root'
})
export class HighlightedCampaignPopupService {

  private readonly storagePrefix = 'mkgo-highlighted-campaign';
  private readonly campaignListStorageSuffix = 'list';
  private readonly autoOpenedCampaignKeys = new Set<string>();

  constructor(private campaignsService: CampaignsService) {}

  async fetchEligibleCampaign(company: Company, user: MkgUser): Promise<HighlightedCampaignEligibility | null> {
    if (!company?.id || !user?.id || !company?.cnpj) {
      return null;
    }

    const eligibility = await this.campaignsService.getEligibleHighlightedCampaign({
      currentState: company.currentState,
      cnpjs: [Utilities.removeMask(company.cnpj)]
    });

    if (eligibility?.campaign) {
      this.persistEligibility(company.id, user.id, eligibility);
      return eligibility;
    }

    return null;
  }

  async fetchAllEligibleCampaigns(company: Company, user: MkgUser): Promise<EligibleCampaignList | null> {
    if (!company?.id || !user?.id || !company?.cnpj) {
      return null;
    }

    const list = await this.campaignsService.getAllEligibleHighlightedCampaigns({
      currentState: company.currentState,
      cnpjs: [Utilities.removeMask(company.cnpj)]
    });

    if (list?.campaigns?.length) {
      this.persistCampaignList(company.id, user.id, list);
    }

    return list ?? null;
  }

  getStoredCampaignList(companyId: string, userId: string): EligibleCampaignList | null {
    if (!companyId?.trim() || !userId?.trim()) {
      return null;
    }

    const rawValue = localStorage.getItem(this.getCampaignListStorageKey(companyId, userId));
    if (!rawValue) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawValue) as StoredEligibleCampaignList;
      return parsed?.list?.campaigns?.length ? parsed.list : null;
    } catch (error) {
      console.error('Erro ao recuperar lista de campanhas armazenada:', error);
      localStorage.removeItem(this.getCampaignListStorageKey(companyId, userId));
      return null;
    }
  }

  getStoredCampaign(companyId: string, userId: string): HighlightedCampaignEligibility | null {
    if (!companyId?.trim() || !userId?.trim()) {
      return null;
    }

    const rawValue = localStorage.getItem(this.getStorageKey(companyId, userId));
    if (!rawValue) {
      return null;
    }

    try {
      const parsedValue = JSON.parse(rawValue) as StoredHighlightedCampaignEligibility;
      return parsedValue?.eligibility?.campaign ? parsedValue.eligibility : null;
    } catch (error) {
      console.error('Erro ao recuperar campanha destacada armazenada:', error);
      localStorage.removeItem(this.getStorageKey(companyId, userId));
      return null;
    }
  }

  shouldAutoOpenCampaign(companyId: string, userId: string, campaign: Campaign | null): boolean {
    const campaignId = this.getCampaignId(campaign);
    if (!companyId?.trim() || !userId?.trim() || !campaignId) {
      return false;
    }

    return !this.autoOpenedCampaignKeys.has(this.getAutoOpenKey(companyId, userId, campaignId));
  }

  markCampaignAsAutoOpened(companyId: string, userId: string, campaign: Campaign | null): void {
    const campaignId = this.getCampaignId(campaign);
    if (!companyId?.trim() || !userId?.trim() || !campaignId) {
      return;
    }

    this.autoOpenedCampaignKeys.add(this.getAutoOpenKey(companyId, userId, campaignId));
  }

  async registerEvent(
    eligibility: HighlightedCampaignEligibility,
    eventType: CampaignEventType,
    eventSource: CampaignEventSource,
    metadata?: CampaignEventMetadata
  ): Promise<void> {
    const campaignId = this.getCampaignId(eligibility?.campaign);
    if (!campaignId) {
      return;
    }

    try {
      await this.campaignsService.registerEvent(campaignId, {
        eventType,
        eventSource,
        deliveryId: eligibility.delivery?.id ?? eligibility.delivery?._id,
        campaignUserStateId: eligibility.userState?.id ?? eligibility.userState?._id,
        metadata
      });
    } catch (error) {
      console.error(`Erro ao registrar evento ${eventType} da campanha destacada:`, error);
    }
  }

  buildWhatsAppLink(campaign: Campaign | null): string | null {
    const phoneNumber = campaign?.whatsappCallToAction?.whatsappPhoneNumber;
    if (!phoneNumber?.trim()) {
      return null;
    }

    try {
      return buildWhatsAppLink(phoneNumber, {
        message: campaign?.whatsappCallToAction?.whatsappMessageTemplate
      });
    } catch (error) {
      console.error('Erro ao gerar link da campanha destacada para o WhatsApp:', error);
      return null;
    }
  }

  private persistEligibility(companyId: string, userId: string, eligibility: HighlightedCampaignEligibility): void {
    const payload: StoredHighlightedCampaignEligibility = {
      storedAt: new Date().toISOString(),
      eligibility
    };

    localStorage.setItem(this.getStorageKey(companyId, userId), JSON.stringify(payload));
  }

  private persistCampaignList(companyId: string, userId: string, list: EligibleCampaignList): void {
    const payload: StoredEligibleCampaignList = {
      storedAt: new Date().toISOString(),
      list
    };

    localStorage.setItem(this.getCampaignListStorageKey(companyId, userId), JSON.stringify(payload));
  }

  private getCampaignId(campaign: Campaign | null | undefined): string | null {
    const campaignId = campaign?.id ?? campaign?._id;
    return campaignId?.trim() ? campaignId : null;
  }

  private getStorageKey(companyId: string, userId: string): string {
    return `${this.storagePrefix}:${companyId}:${userId}`;
  }

  private getCampaignListStorageKey(companyId: string, userId: string): string {
    return `${this.storagePrefix}:${this.campaignListStorageSuffix}:${companyId}:${userId}`;
  }

  private getAutoOpenKey(companyId: string, userId: string, campaignId: string): string {
    return `${this.storagePrefix}:auto-opened:${companyId}:${userId}:${campaignId}`;
  }
}